import classnames from 'classnames'
import CardHeader from 'components/lib/CardHeader'
import { Close, Send } from 'components/lib/icons'
import NakedButton from 'components/lib/NakedButton'
import TextArea from 'components/lib/TextArea'
import { EMOJI_RATINGS } from 'constants/conversationRatings'
import useKeyPress from 'hooks/useKeyPress'
import usePrevious from 'hooks/usePrevious'
import useThemeColors from 'hooks/useThemeColors'
import { sendMessage } from 'modules/conversations/actions'
import { createMessage } from 'modules/message/creators'
import { getMessageAttribute } from 'modules/message/utils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Message, MessageContentType, MessageType } from 'types/message'

import './style.css'

const isValidMessage = (text: string) => text && text.trim().length > 0

interface RateConversationProps {
  message: Message
}

const RateConversation = ({ message }: RateConversationProps) => {
  const { t } = useTranslation()
  const { activeColor, widgetSecondaryColor } = useThemeColors()
  const prevMessage = usePrevious(message)

  const dispatch = useDispatch()
  const { endUser } = useSelector(state => state.session)

  const [isOpen, setIsOpen] = useState(true)
  const [ratingText, setRatingText] = useState('')
  const [ratingValue, setRatingValue] = useState(0)

  const {
    ratingPromptMessage,
    ratingAcknowledgement
  } = useMemo(() => getMessageAttribute(message, 'ratingPrompt'), [message])

  const pressedEnter = useKeyPress({
    targetKey: 'Enter'
  })

  const submitConversationRating = useCallback(() => {
    const messageToSend = createMessage({
      message: '',
      activeConversation: message.conversationId,
      authorId: endUser.id,
      attributes: {
        conversationRating: {
          ratingPrompt: {
            ratingAcknowledgement,
            ratingPromptMessage: ratingPromptMessage.trim()
          },
          ratingValue,
          ratingText
        }
      },
      contentType: MessageContentType.BOT_CONVERSATION_RATING,
      type: MessageType.BOT_CONVERSATION_RATING
    })
    dispatch(sendMessage({ message: messageToSend }))
    setIsOpen(false)
  }, [message, dispatch, endUser.id, ratingAcknowledgement, ratingPromptMessage, ratingText, ratingValue])

  useEffect(() => {
    pressedEnter && isValidMessage(ratingText) && submitConversationRating()
  }, [pressedEnter, ratingText, submitConversationRating])

  useEffect(() => {
    if (prevMessage && prevMessage !== message && !isOpen) {
      setIsOpen(true)
    }
  }, [message, prevMessage, isOpen])

  const ratings = useMemo(() => {
    return EMOJI_RATINGS.map((emojiRating) => {
      const { emoji, value } = emojiRating
      return (
        <NakedButton
          className={classnames('drift-widget-rate-conversation--rating', {
            'drift-widget-rate-conversation--rating--selected': value === ratingValue
          })}
          key={`rating_${value}`}
          aria-label={`rate ${value}`}
          onClick={() => setRatingValue(value)}
        >
          {emoji}
        </NakedButton>
      )
    })
  }, [ratingValue])

  if (!isOpen) {
    return null
  }

  const onTextAreaValueChange = (value: string) => {
    if (pressedEnter) {
      return
    }

    setRatingText(value)
  }

  return (
    <>
      <div className="drift-widget-rate-conversation--cover" />

      <div className='drift-widget-rate-conversation'>
        <CardHeader>
          <span>{ratingPromptMessage}</span>
          <NakedButton
            className="drift-widget-rate-conversation--close-button"
            aria-label="close rating"
            onClick={() => setIsOpen(false)}
        >
            <Close width={20} height={20} fill={widgetSecondaryColor} />
          </NakedButton>
        </CardHeader>

        <div className="drift-widget-rate-conversation--body">
          <div className="drift-widget-rate-conversation--ratings">
            {ratings}
          </div>
          {!!ratingValue &&
            <div className="drift-widget-rate-conversation--composer">
              <div className="drift-widget-rate-conversation--actions">
                <NakedButton
                  className='drift-widget-rate-conversation--send-button'
                  onClick={submitConversationRating}
                >
                  <Send color={`#${activeColor}`} />
                </NakedButton>
              </div>
              <TextArea
                aria-label='rating input'
                maxLength={4096}
                placeholder={t('form.placeholder.tellUsMore')}
                onValueChange={onTextAreaValueChange}
                value={ratingText}
              />
            </div>
          }
        </div>
      </div>
    </>
  )
}

export default RateConversation
